import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tasks, directives, clients, aiTests } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import VersionHistoryModal from '../../components/VersionHistoryModal'

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) {
    const match = dateString.match(/^(\d{4}-\d{2}-\d{2})/)
    if (match) {
      return new Date(match[1]).toLocaleDateString()
    }
    return ''
  }
  return date.toLocaleDateString()
}

// ==================== Tasks Tab ====================

function TaskStatusBadge({ status, isOverdue }) {
  const colors = {
    pending: isOverdue ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-800',
  }
  const labels = {
    pending: isOverdue ? 'Overdue' : 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  }
  return (
    <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {labels[status] || status}
    </span>
  )
}

function NewTaskModal({ isOpen, onClose, onSubmit, clientsList }) {
  const [task, setTask] = useState('')
  const [clientId, setClientId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [notes, setNotes] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit({
      task,
      clientId: clientId ? parseInt(clientId) : null,
      dueDate: dueDate || null,
      notes: notes || null,
    })
    setTask('')
    setClientId('')
    setDueDate('')
    setNotes('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Task</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
            <textarea
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder="What needs to be done?"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client (optional)</label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">No specific client</option>
              {clientsList?.map(client => (
                <option key={client.id} value={client.id}>{client.fullName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional context or details..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TasksTab() {
  const [showNewModal, setShowNewModal] = useState(false)
  const [filter, setFilter] = useState('active')
  const queryClient = useQueryClient()

  const { data: taskData, isLoading } = useQuery({
    queryKey: ['tasks', filter],
    queryFn: () => tasks.list(filter === 'active' ? null : filter),
  })

  const { data: clientData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clients.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data) => tasks.create(data.task, data.clientId, data.dueDate, data.notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setShowNewModal(false)
    },
  })

  const completeMutation = useMutation({
    mutationFn: (id) => tasks.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => tasks.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const tasksList = taskData?.tasks || []
  const clientsList = clientData?.Client || []

  return (
    <>
      {/* Filter Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="flex">
          {[
            { key: 'active', label: 'Active' },
            { key: 'pending', label: 'Pending' },
            { key: 'overdue', label: 'Overdue' },
            { key: 'completed', label: 'Completed' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`flex-1 py-3 text-sm font-medium ${filter === tab.key ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Task List */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : tasksList.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <p>No {filter} tasks</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="mt-4 text-primary-600 hover:text-primary-700"
            >
              Create a new task
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {tasksList.map(task => (
              <div key={task.id} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  {task.status !== 'completed' && (
                    <button
                      onClick={() => completeMutation.mutate(task.id)}
                      className="mt-1 w-5 h-5 rounded border-2 border-gray-300 hover:border-primary-500 flex items-center justify-center flex-shrink-0"
                      title="Mark as complete"
                    >
                      <svg className="w-3 h-3 text-transparent hover:text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </button>
                  )}
                  {task.status === 'completed' && (
                    <div className="mt-1 w-5 h-5 rounded bg-green-500 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <TaskStatusBadge status={task.status} isOverdue={task.isOverdue} />
                      {task.dueDate && (
                        <span className={`text-xs ${task.isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                          Due: {formatDate(task.dueDate)}
                        </span>
                      )}
                    </div>
                    <p className={`font-medium ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {task.task}
                    </p>
                    {task.client && (
                      <p className="text-sm text-primary-600 mt-1">
                        Client: {task.client.fullName}
                      </p>
                    )}
                    {task.notes && (
                      <p className="text-sm text-gray-600 mt-1">{task.notes}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Created: {formatDate(task.createdAt)}
                      {task.completedAt && ` | Completed: ${formatDate(task.completedAt)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm('Delete this task?')) {
                        deleteMutation.mutate(task.id)
                      }
                    }}
                    className="text-gray-400 hover:text-red-500 flex-shrink-0"
                    title="Delete task"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <NewTaskModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSubmit={createMutation.mutate}
        clientsList={clientsList}
      />

      {/* Floating Add Button */}
      <button
        onClick={() => setShowNewModal(true)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 flex items-center justify-center"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </>
  )
}

// ==================== Directives Tab ====================

const CATEGORY_LABELS = {
  identity: 'Identity',
  methodology: 'Methodology',
  anti_patterns: 'Anti-Patterns',
  onboarding: 'Onboarding',
  instructions: 'Instructions',
  protocol: 'Protocol',
  knowledge: 'Knowledge',
  capabilities: 'Capabilities',
  writing_style: 'Writing Style',
  trainer_participation: 'Trainer',
  general: 'General',
}

function CategoryBadge({ category }) {
  if (!category) return null
  const label = CATEGORY_LABELS[category] || category
  return (
    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 dark:bg-dial-elevated dark:text-indices-muted">
      {label}
    </span>
  )
}

function NewDirectiveModal({ isOpen, onClose, onSubmit }) {
  const [directive, setDirective] = useState('')
  const [priority, setPriority] = useState(0)
  const [scope, setScope] = useState('client')

  const isEmptyText = !directive.trim()

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEmptyText) return
    onSubmit({ directive, priority, scope })
    setDirective('')
    setPriority(0)
    setScope('client')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">New AI Directive</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Directive</label>
            <textarea
              value={directive}
              onChange={(e) => setDirective(e.target.value)}
              placeholder="Enter an instruction for the AI to follow in all conversations..."
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Use {'{trainerName}'} and {'{trainerFirstName}'} as placeholders for runtime substitution.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={0}>Normal</option>
              <option value={1}>High</option>
              <option value={2}>Critical</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Higher priority directives are emphasized more strongly to the AI
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="client">Client — injected into client AI chats</option>
              <option value="trainer">Trainer — injected into trainer AI chat only</option>
              <option value="all">All — injected into both</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Controls which AI conversations this directive applies to
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isEmptyText}
              className="flex-1 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Directive
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function DirectiveCard({ dir, onToggle, onDeactivate, onHistory }) {
  const [expanded, setExpanded] = useState(false)
  const prio = priorityLabel(dir.priority)
  const isLong = dir.directive && dir.directive.length > 200

  return (
    <div className={`bg-white rounded-xl p-4 shadow-sm ${!dir.isActive ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-3">
        <button
          onClick={() => onToggle(dir)}
          className={`mt-1 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${dir.isActive ? 'bg-green-500' : 'border-2 border-gray-300'}`}
          title={dir.isActive ? 'Deactivate' : 'Activate'}
        >
          {dir.isActive && (
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`px-2 py-0.5 text-xs rounded-full ${prio.color}`}>
              {prio.label}
            </span>
            <CategoryBadge category={dir.category} />
            {dir.scope && dir.scope !== 'client' && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${dir.scope === 'trainer' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                {dir.scope === 'trainer' ? 'Trainer only' : 'All'}
              </span>
            )}
            {!dir.isActive && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">
                Inactive
              </span>
            )}
          </div>
          <div
            className={`text-gray-900 whitespace-pre-wrap text-sm ${!expanded && isLong ? 'line-clamp-3' : ''}`}
          >
            {dir.directive}
          </div>
          {isLong && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-primary-600 hover:text-primary-700 mt-1"
            >
              {expanded ? 'Show less' : 'Show more'}
            </button>
          )}
          <p className="text-xs text-gray-400 mt-2">
            Created: {formatDate(dir.createdAt)}
          </p>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => onHistory(dir)}
            className="text-gray-400 hover:text-blue-500"
            title="Version history"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          {dir.isActive ? (
            <button
              onClick={() => onDeactivate(dir)}
              className="text-gray-400 hover:text-orange-500"
              title="Deactivate directive"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => onToggle(dir)}
              className="text-gray-400 hover:text-green-500"
              title="Activate directive"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function priorityLabel(p) {
  if (p >= 2) return { label: 'Critical', color: 'bg-red-100 text-red-800' }
  if (p >= 1) return { label: 'High', color: 'bg-orange-100 text-orange-800' }
  return { label: 'Normal', color: 'bg-gray-100 text-gray-600' }
}

function DirectivesTab() {
  const [showNewModal, setShowNewModal] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [historyDirectiveId, setHistoryDirectiveId] = useState(null)
  const queryClient = useQueryClient()

  const { data: directiveData, isLoading } = useQuery({
    queryKey: ['directives', showAll],
    queryFn: () => directives.list(showAll),
  })

  const createMutation = useMutation({
    mutationFn: (data) => directives.create(data.directive, data.priority, data.scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directives'] })
      setShowNewModal(false)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => directives.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['directives'] })
    },
  })

  const directivesList = directiveData?.directives || []

  const handleToggle = (dir) => {
    toggleMutation.mutate({ id: dir.id, isActive: !dir.isActive })
  }

  const handleDeactivate = (dir) => {
    if (confirm('Deactivate this directive? It can be reactivated later.')) {
      toggleMutation.mutate({ id: dir.id, isActive: false })
    }
  }

  return (
    <>
      {/* Toggle for showing inactive */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {directivesList.length} directive{directivesList.length !== 1 ? 's' : ''}
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Show inactive
        </label>
      </div>

      {/* Directive List */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : directivesList.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No directives configured</p>
            <p className="text-sm mt-1">Directives are persistent instructions the AI will follow in all conversations</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="mt-4 text-primary-600 hover:text-primary-700"
            >
              Add your first directive
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {directivesList.map(dir => (
              <DirectiveCard
                key={dir.id}
                dir={dir}
                onToggle={handleToggle}
                onDeactivate={handleDeactivate}
                onHistory={(d) => setHistoryDirectiveId(d.id)}
              />
            ))}
          </div>
        )}
      </div>

      <NewDirectiveModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSubmit={createMutation.mutate}
      />

      <VersionHistoryModal
        isOpen={!!historyDirectiveId}
        onClose={() => {
          setHistoryDirectiveId(null)
          queryClient.invalidateQueries({ queryKey: ['directives'] })
        }}
        entityId={historyDirectiveId}
        entityType="directive"
        api={directives}
        queryKeyPrefix="directives"
        renderContent={(v) => (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-indices-muted">Priority:</span>
              <span className="text-indices-bright">{v.priority >= 2 ? 'Critical' : v.priority >= 1 ? 'High' : 'Normal'}</span>
              <span className="text-indices-muted ml-2">Scope:</span>
              <span className="text-indices-bright">{v.scope}</span>
              {v.category && (
                <>
                  <span className="text-indices-muted ml-2">Category:</span>
                  <span className="text-indices-bright">{v.category}</span>
                </>
              )}
            </div>
            <div>
              <span className="text-indices-muted">Active: </span>
              <span className={v.isActive ? 'text-green-400' : 'text-red-400'}>
                {v.isActive ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="mt-2">
              <span className="text-indices-muted text-xs block mb-1">Directive text:</span>
              <pre className="text-lume-teal bg-dial-black rounded-lg p-3 text-xs whitespace-pre-wrap max-h-48 overflow-y-auto border border-dial-border">
                {v.directive}
              </pre>
            </div>
          </div>
        )}
      />

      {/* Floating Add Button */}
      <button
        onClick={() => setShowNewModal(true)}
        className="fixed bottom-20 right-4 w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 flex items-center justify-center"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </>
  )
}

// ==================== Context Tab ====================

function ContextTab() {
  return (
    <div className="p-4 space-y-6">
      {/* Client Chat System Prompt */}
      <div className="bg-white dark:bg-dial-surface rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-status-info/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600 dark:text-status-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 dark:text-indices-bright">Client Chat System Prompt</h4>
        </div>
        <p className="text-xs text-gray-500 dark:text-indices-muted mb-3">
          This is the structure of what's sent to Claude at the start of each client conversation. Behavioral sections (identity, methodology, protocols, etc.) are now loaded from Directives.
        </p>

        {/* System Prompt Preview */}
        <div className="bg-gray-50 dark:bg-dial-black rounded-lg p-3 font-mono text-xs space-y-3 overflow-x-auto">
          <div>
            <span className="text-yellow-600 dark:text-yellow-500 font-semibold">TRAINER BACKGROUND:</span>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              Name: <span className="text-green-600 dark:text-lume-teal">[Trainer Name]</span><br/>
              <span className="text-green-600 dark:text-lume-teal">[Trainer Bio]</span><br/>
              Specialties: <span className="text-green-600 dark:text-lume-teal">[Trainer Specialties]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-yellow-600 dark:text-yellow-500 font-semibold">CLIENT PROFILE:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              Name: <span className="text-green-600 dark:text-lume-teal">[Client Name]</span><br/>
              Training Type: <span className="text-green-600 dark:text-lume-teal">[Training Type]</span><br/>
              Goals: <span className="text-green-600 dark:text-lume-teal">[Client Goals]</span><br/>
              Medical Notes (IMPORTANT): <span className="text-green-600 dark:text-lume-teal">[Medical Notes]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-yellow-600 dark:text-yellow-500 font-semibold">CONVERSATION TYPE:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              <span className="text-green-600 dark:text-lume-teal">[training / nutrition / general]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-gray-600 dark:text-indices-medium">Calendar & Stats Context:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              - Weight, body fat, upcoming workouts, recent sessions
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-red-600 dark:text-status-error font-semibold">CRITICAL CLIENT INFORMATION:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              - <span className="text-green-600 dark:text-lume-teal">[Critical memories auto-loaded]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-yellow-600 dark:text-yellow-500 font-semibold">IMPORTANT CLIENT NOTES:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              - [type] <span className="text-green-600 dark:text-lume-teal">[Pinned memories, up to 5]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-yellow-600 dark:text-yellow-500 font-semibold">RECENT COACHING OBSERVATIONS:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              - [type] <span className="text-green-600 dark:text-lume-teal">[Last 5 coaching notes]</span>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-purple-600 dark:text-purple-400 font-semibold">TRAINER DIRECTIVES (MUST FOLLOW):</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2 text-[10px]">
              <span className="text-green-600 dark:text-lume-teal">[All active directives loaded here]</span><br/>
              Identity, Methodology, Anti-Patterns, Instructions, Protocols,<br/>
              Knowledge Usage, Capabilities, Writing Style, Trainer Participation<br/>
              + Onboarding (if new client) + Client-specific directives
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-dial-border pt-2">
            <div className="text-yellow-600 dark:text-yellow-500 font-semibold">RELEVANT TRAINING KNOWLEDGE:</div>
            <div className="text-gray-600 dark:text-indices-medium pl-2">
              ### <span className="text-green-600 dark:text-lume-teal">[Knowledge Article Title]</span><br/>
              <span className="text-green-600 dark:text-lume-teal">[Article content, truncated to 4000 chars]</span>
            </div>
          </div>
        </div>
      </div>

      {/* Available Tools */}
      <div className="bg-white dark:bg-dial-surface rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 dark:text-indices-bright">Available Tools (22 total)</h4>
        </div>
        <p className="text-xs text-gray-500 dark:text-indices-muted mb-3">
          Tools the AI can call to fetch data or take actions on behalf of the client.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {/* Read Tools */}
          <div className="bg-gray-50 dark:bg-dial-black rounded-lg p-2">
            <div className="text-blue-600 dark:text-status-info font-semibold mb-1">Read Tools</div>
            <div className="space-y-1 text-gray-600 dark:text-indices-medium font-mono">
              <div>get_client_stats</div>
              <div>get_workout_history</div>
              <div>get_assigned_workouts</div>
              <div>get_exercise_details</div>
              <div>get_exercise_progress</div>
              <div>get_progress_photos</div>
              <div>get_my_summary</div>
              <div>search_knowledge</div>
              <div>get_conversation_history</div>
              <div>get_meal_plan</div>
              <div>get_meal_logs</div>
              <div>lookup_nutrition</div>
            </div>
          </div>

          {/* Write Tools */}
          <div className="bg-gray-50 dark:bg-dial-black rounded-lg p-2">
            <div className="text-green-600 dark:text-lume-teal font-semibold mb-1">Write Tools</div>
            <div className="space-y-1 text-gray-600 dark:text-indices-medium font-mono">
              <div>add_coaching_note</div>
              <div>log_weight</div>
              <div>log_body_measurements</div>
              <div>update_client_goals</div>
              <div>mark_workout_complete</div>
              <div>log_meal</div>
            </div>
            <div className="text-purple-600 dark:text-purple-400 font-semibold mt-2 mb-1">Memory Tools</div>
            <div className="space-y-1 text-gray-600 dark:text-indices-medium font-mono">
              <div>save_memory</div>
              <div>recall_memories</div>
              <div>get_coaching_notes</div>
            </div>
            <div className="text-red-600 dark:text-status-error font-semibold mt-2 mb-1">Emergency</div>
            <div className="space-y-1 text-gray-600 dark:text-indices-medium font-mono">
              <div>emergency_alert</div>
            </div>
          </div>
        </div>
      </div>

      {/* Trainer Chat Note */}
      <div className="bg-white dark:bg-dial-surface rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-lume-teal/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary-600 dark:text-lume-teal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h4 className="font-semibold text-gray-900 dark:text-indices-bright">Trainer Chat (Different System)</h4>
        </div>
        <p className="text-sm text-gray-600 dark:text-indices-medium">
          The Trainer Chat uses a separate system with its own tools for managing clients, workouts, knowledge base,
          exercises, meal plans, and more. It operates at the trainer level rather than per-client.
        </p>
      </div>

      {/* Emergency Protocol */}
      <div className="bg-red-50 dark:bg-status-error/10 rounded-xl p-4 border border-red-200 dark:border-status-error/30">
        <div className="flex items-center gap-2 mb-2">
          <svg className="w-5 h-5 text-red-600 dark:text-status-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h4 className="font-medium text-red-800 dark:text-status-error">Emergency Protocol</h4>
        </div>
        <p className="text-sm text-red-700 dark:text-status-error/80">
          Client chat AI is equipped with emergency detection. If a client reports chest pain, difficulty breathing,
          severe symptoms, or other medical emergencies, the AI will immediately trigger an emergency alert to notify
          you and direct the client to seek medical help.
        </p>
      </div>

      {/* Tips */}
      <div className="bg-blue-50 dark:bg-status-info/10 rounded-xl p-4 border border-blue-200 dark:border-status-info/30">
        <h4 className="font-medium text-blue-800 dark:text-status-info mb-2">Tips for Better AI Responses</h4>
        <ul className="space-y-1 text-sm text-blue-700 dark:text-status-info/80">
          <li>• Mark important client memories as "critical" or "pinned" so AI always sees them</li>
          <li>• Add coaching notes before sessions to give AI relevant context</li>
          <li>• Keep knowledge base updated with your training philosophy</li>
          <li>• Use directives to customize AI behavior — all coaching personality is now editable here</li>
          <li>• Detailed client profiles = more personalized AI responses</li>
        </ul>
      </div>
    </div>
  )
}

// ==================== Tests Tab ====================

function TestsTab() {
  const [results, setResults] = useState(null)
  const [running, setRunning] = useState(false)
  const [runningId, setRunningId] = useState(null) // null = all, string = single

  const { data: scenarioData } = useQuery({
    queryKey: ['aiTestScenarios'],
    queryFn: () => aiTests.list(),
  })

  const scenarios = scenarioData?.scenarios || []

  const runTests = async (scenarioId = null) => {
    setRunning(true)
    setRunningId(scenarioId)
    setResults(null)
    try {
      const data = await aiTests.run(scenarioId)
      setResults(data)
    } catch (err) {
      setResults({ error: err.message })
    } finally {
      setRunning(false)
      setRunningId(null)
    }
  }

  const summary = results?.summary

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="bg-white dark:bg-dial-surface rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-indices-bright">AI Behavior Tests</h3>
            <p className="text-xs text-gray-500 dark:text-indices-muted mt-1">
              Run scenarios to verify directives and KB produce correct AI behavior. Uses Sonnet for cost efficiency (~$0.50/run).
            </p>
          </div>
          <button
            onClick={() => runTests()}
            disabled={running}
            className="px-4 py-2 bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black rounded-lg hover:bg-primary-700 dark:hover:bg-lume-green disabled:opacity-50 text-sm font-medium flex items-center gap-2"
          >
            {running && !runningId ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white dark:border-dial-black border-t-transparent" />
                Running...
              </>
            ) : (
              'Run All Tests'
            )}
          </button>
        </div>

        {/* Summary */}
        {summary && (
          <div className={`rounded-lg p-3 ${summary.failedScenarios === 0 ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-3 text-sm">
              <span className={summary.failedScenarios === 0 ? 'text-green-700 dark:text-green-400 font-medium' : 'text-red-700 dark:text-red-400 font-medium'}>
                {summary.failedScenarios === 0 ? 'All tests passed' : `${summary.failedScenarios} scenario(s) failed`}
              </span>
              <span className="text-gray-500 dark:text-indices-muted">
                {summary.passedChecks}/{summary.totalChecks} checks passed
              </span>
              {results.model && (
                <span className="text-gray-400 dark:text-indices-muted text-xs">
                  Model: {results.model}
                </span>
              )}
            </div>
          </div>
        )}

        {results?.error && !results.scenarios && (
          <div className="rounded-lg p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
            {results.error}
          </div>
        )}
      </div>

      {/* Scenario Cards */}
      {scenarios.map(scenario => {
        const result = results?.scenarios?.find(r => r.id === scenario.id)
        const isRunningThis = running && (runningId === scenario.id || runningId === null)

        return (
          <div key={scenario.id} className="bg-white dark:bg-dial-surface rounded-xl shadow-sm overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {result && (
                    <span className={`w-2 h-2 rounded-full ${result.passed ? 'bg-green-500' : 'bg-red-500'}`} />
                  )}
                  <h4 className="font-medium text-gray-900 dark:text-indices-bright">{scenario.name}</h4>
                  {result && (
                    <span className="text-xs text-gray-400 dark:text-indices-muted">
                      {(result.durationMs / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>
                <button
                  onClick={() => runTests(scenario.id)}
                  disabled={running}
                  className="text-xs px-3 py-1 rounded-md bg-gray-100 dark:bg-dial-elevated text-gray-600 dark:text-indices-medium hover:bg-gray-200 dark:hover:bg-dial-border disabled:opacity-50"
                >
                  {isRunningThis && runningId === scenario.id ? 'Running...' : 'Run'}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-indices-muted">{scenario.description}</p>
              <p className="text-xs text-gray-400 dark:text-indices-muted mt-1">{scenario.checkCount} checks</p>

              {/* Check Results */}
              {result && result.checks && result.checks.length > 0 && (
                <div className="mt-3 space-y-1">
                  {result.checks.map(check => (
                    <div key={check.id} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 flex-shrink-0 ${check.passed ? 'text-green-500' : 'text-red-500'}`}>
                        {check.passed ? '\u2713' : '\u2717'}
                      </span>
                      <div className="min-w-0">
                        <span className={check.passed ? 'text-gray-700 dark:text-indices-medium' : 'text-red-700 dark:text-red-400 font-medium'}>
                          {check.description}
                        </span>
                        {!check.passed && check.detail && (
                          <p className="text-gray-400 dark:text-indices-muted mt-0.5 truncate">{check.detail}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error */}
              {result && result.error && (
                <div className="mt-3 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2">
                  {result.error}
                </div>
              )}

              {/* Raw Response Preview */}
              {result && result.rawResponse && (
                <details className="mt-3">
                  <summary className="text-xs text-gray-400 dark:text-indices-muted cursor-pointer hover:text-gray-600 dark:hover:text-indices-medium">
                    Response preview
                  </summary>
                  <pre className="mt-1 text-xs text-gray-500 dark:text-indices-muted bg-gray-50 dark:bg-dial-black rounded p-2 whitespace-pre-wrap max-h-40 overflow-y-auto">
                    {result.rawResponse}
                  </pre>
                </details>
              )}
            </div>
          </div>
        )
      })}

      {/* Loading shimmer when running with no previous results */}
      {running && !results && scenarios.length === 0 && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-lume-teal" />
        </div>
      )}
    </div>
  )
}

// ==================== Main Component ====================

export default function AISettingsPage() {
  const [activeTab, setActiveTab] = useState('directives')

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-dial-black">
      <PageHeader
        title="AI Settings"
        showBack
      />

      {/* Main Tabs */}
      <div className="bg-white dark:bg-dial-surface border-b border-gray-200 dark:border-dial-border">
        <div className="flex">
          <button
            onClick={() => setActiveTab('directives')}
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'directives' ? 'text-primary-600 dark:text-lume-teal border-b-2 border-primary-600 dark:border-lume-teal' : 'text-gray-500 dark:text-indices-muted'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Directives
          </button>
          <button
            onClick={() => setActiveTab('tasks')}
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'tasks' ? 'text-primary-600 dark:text-lume-teal border-b-2 border-primary-600 dark:border-lume-teal' : 'text-gray-500 dark:text-indices-muted'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            Tasks
          </button>
          <button
            onClick={() => setActiveTab('context')}
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'context' ? 'text-primary-600 dark:text-lume-teal border-b-2 border-primary-600 dark:border-lume-teal' : 'text-gray-500 dark:text-indices-muted'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Context
          </button>
          <button
            onClick={() => setActiveTab('tests')}
            className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'tests' ? 'text-primary-600 dark:text-lume-teal border-b-2 border-primary-600 dark:border-lume-teal' : 'text-gray-500 dark:text-indices-muted'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Tests
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'directives' && <DirectivesTab />}
      {activeTab === 'tasks' && <TasksTab />}
      {activeTab === 'context' && <ContextTab />}
      {activeTab === 'tests' && <TestsTab />}
    </div>
  )
}
